import type { InteractionWithMetadata } from '@vagusx/clients';
import { OUTBOUND_CALL_OUTCOME, OUTBOUND_CALL_OUTCOME_LABELS } from '@vagusx/shared';
import { dayEndToUtcIso, dayStartToUtcIso, type ResolvedDateRange } from '@/lib/date-range';

/**
 * Presentation and query helpers for the Call History page. Pure, so the
 * bounds arithmetic can be tested without rendering anything.
 */

/**
 * Who the call was with.
 *
 * The list resolves a patient by chart id OR by a matching phone number, so
 * a name is present more often than `patientId` alone would suggest. When
 * there is none, the stand-in depends on direction: an inbound call from a
 * number on no chart is genuinely an unknown caller, while an outbound call
 * always had a chart to dial from, so a missing name there is a data problem
 * worth naming as one.
 */
export function callerName(row: Pick<InteractionWithMetadata, 'patient' | 'direction'>): string {
  const name = [row.patient?.firstName, row.patient?.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;

  return row.direction === 'outbound' ? 'Unnamed patient' : 'Unknown caller';
}

/**
 * Turn a resolved `yyyy-MM-dd` window into the ISO instants the list endpoint
 * compares against `started_at`.
 *
 * Both bounds are INCLUSIVE whole days in the reader's own timezone, which is
 * the only reading of "Sep 1 – Sep 30" anybody intends — hence
 * {@link dayEndToUtcIso} for the upper one. Taking the upper date at face
 * value would make it midnight, so a single-day window would match only calls
 * placed in the day's first instant: an empty table that reads as "no calls
 * that day" rather than as an off-by-one.
 *
 * A `null` window (All time, or a half-picked custom range) bounds nothing.
 */
export function callDateBounds(range: ResolvedDateRange | null): {
  startDate?: string;
  endDate?: string;
} {
  if (!range) return { startDate: undefined, endDate: undefined };
  return {
    startDate: dayStartToUtcIso(range.from),
    endDate: dayEndToUtcIso(range.to),
  };
}

const OUTCOME_LABELS: Record<string, string> = Object.fromEntries(
  OUTBOUND_CALL_OUTCOME_LABELS.map((o) => [o.value, o.label])
);

/**
 * The Outcome cell's text. The shared labels are written for outbound, and one
 * of them is wrong inbound: an inbound `failed` is a session that crashed while
 * the caller was on the line, not a call that never connected. It reads the
 * same as the evaluator's headline for that call (ADR-259).
 */
export function outcomeLabel(
  row: Pick<InteractionWithMetadata, 'outcomeCategory' | 'direction'>
): string | null {
  const outcome = row.outcomeCategory;
  if (!outcome) return null;
  if (row.direction === 'inbound' && outcome === OUTBOUND_CALL_OUTCOME.FAILED) {
    return 'Ended early';
  }
  return OUTCOME_LABELS[outcome] ?? outcome;
}
